import React, { ReactElement, useCallback, useContext, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { productService, warmImageConnections } from '../../services/product.service'
import { Product } from '../../interfaces/definitions/product.interface'
import './ProductDetails.css'
import StepDetails from '../StepDetails/StepDetails'
import { allSteps, Step } from '../../types/step-name'
import { FiArrowLeft, FiTrash } from 'react-icons/fi'
import { Bounce } from 'react-activity'
import { ProductContext } from '../../context/ProductContext'
import { PartNumberContext } from '../../context/PartNumberContext'
import { LoadingContext } from '../../context/LoadingContext'
import { PermissionContext } from '../../context/PermissionContext'

export default function ProductDetails (): ReactElement {
  const { id } = useParams()
  const [activeTab, setActiveTab] = useState<Step>('incoming')
  const { product: prod, setProduct: setProd } = useContext(ProductContext)
  const { setPartNumber } = useContext(PartNumberContext)
  const { isLoading, setIsLoading } = useContext(LoadingContext)
  const { admin } = useContext(PermissionContext)
  const navigate = useNavigate()

  // The product context is app-global and outlives this page, so an order
  // opened earlier would flash here while the new one is still loading (the
  // in-app back button clears it, the browser back button does not). Render
  // only a product that matches the route, and clear the context on the way
  // out no matter how the user navigated.
  const product = prod !== null && prod.id === id ? prod : null

  useEffect(() => {
    return () => {
      setProd(null)
    }
  }, [])

  const fetchProd = useCallback(async () => {
    if (id === undefined) return
    const prod = await productService.getProducts({
      mode: 'one',
      id
    }) as Product

    setProd(prod)
    setActiveTab(prod.currentStep)
  }, [id])

  useEffect(() => {
    warmImageConnections()
    fetchProd().catch(error => {
      console.error(error)
    })

    console.log(admin)
  }, [fetchProd, admin])

  const handleOnDeleteProduct = async (productId: string | undefined): Promise<void> => {
    setIsLoading(true)
    if (id === undefined) return
    try {
      const steps: Step[] = allSteps
      await productService.deleteProduct(productId as string, steps)
    } catch (error) {
      console.error('Error deleting all product:', error)
    } finally {
      setIsLoading(false)
      navigate('/')
    }
  }

  if (isLoading) {
    return (
      <div className='product-details'>
        <Bounce color='#4285f4' size={50} className='loader' />
      </div>
    )
  }

  return (
    <div className='product-details'>

      <div className='product-details-header'>
        <button
          type='button'
          title='Go Back'
          className='back-button' onClick={() => {
            setProd(null)
            setPartNumber(undefined)
            window.history.back()
          }}
        >
          <FiArrowLeft />
        </button>

        {(product == null) && <Bounce color='#4285f4' size={50} className='loader' />}

        {product != null && (
          <div className='tabs'>
            {((product?.images.length > 0) || product.currentStep === 'inProcess') && (
              <button
                className={activeTab === 'incoming' ? 'active incoming' : 'incoming'}
                onClick={() => setActiveTab('incoming')}
              >
                Incoming
              </button>
            )}
            {((product.inProcess != null) || product.currentStep === 'inProcess') && (
              <button
                className={activeTab === 'inProcess' ? 'active inProcess' : 'inProcess'}
                onClick={() => setActiveTab('inProcess')}
              >
                InProcess
              </button>
            )}
            {(((product?.outgoing) != null) || product.currentStep === 'outgoing') && (
              <button
                className={activeTab === 'outgoing' ? 'active outgoing' : 'outgoing'}
                onClick={() => setActiveTab('outgoing')}
              >
                Outgoing
              </button>
            )}
            {((product?.scrapped) != null || product.currentStep === 'scrapped') && (
              <button
                className={activeTab === 'scrapped' ? 'active scrapped' : 'scrapped'}
                onClick={() => setActiveTab('scrapped')}
              >
                Scrapped
              </button>
            )}
          </div>)}

        {admin === true && (
          <button
            type='button'
            title='Delete Work Order'
            className='delete-button'
            onClick={() => {
              const deleteProduct = async (): Promise<void> => {
                if (window.confirm('Are you sure you want to delete this work order?')) {
                  await handleOnDeleteProduct(id)
                }
              }
              deleteProduct().catch(console.error)
            }}
          >
            <FiTrash size={24} />
          </button>
        )}

      </div>

      {activeTab === 'incoming' && (
        <>
          {(product != null) && (
            <StepDetails
              description={product?.description}
              images={product?.images}
            />
          )}
        </>
      )}
      {activeTab === 'inProcess' && (
        <>
          {(product?.inProcess?.images != null) && (
            <StepDetails
              description={product.inProcess?.description}
              images={product.inProcess?.images}
            />
          )}
        </>
      )}
      {activeTab === 'outgoing' && (
        <>
          {(product?.outgoing?.images != null) && (
            <StepDetails
              description={product.outgoing?.description}
              images={product.outgoing?.images}
            />
          )}
        </>
      )}
      {activeTab === 'scrapped' && (
        <>
          {(product?.scrapped?.images != null) && (
            <StepDetails
              description={product.scrapped?.description}
              images={product.scrapped?.images}
            />
          )}
        </>
      )}
    </div>
  )
}
